import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useScroll, useTransform } from 'framer-motion';
import ThemeToggle from '../components/ThemeToggle';
import '../styles/landing.css';

import {
  HiOutlineFilm,
  HiOutlineCollection,
  HiOutlineGlobe,
  HiOutlineMoon,
  HiOutlineDeviceMobile,
  HiOutlineShieldCheck,
  HiOutlineCog,
  HiOutlineSearch,
  HiOutlineMail,
  HiOutlineMenu,
  HiOutlineX,
  HiOutlineCurrencyDollar,
} from 'react-icons/hi';

/* ==========================================
   ANIMATION VARIANTS (spring-based for fluidity)
   ========================================== */
const spring = { type: 'spring', stiffness: 100, damping: 18 };
const springSnappy = { type: 'spring', stiffness: 120, damping: 20 };

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { ...spring, delay },
  }),
};

const fadeDown = {
  hidden: { opacity: 0, y: -20 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { ...spring, delay },
  }),
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: (delay = 0) => ({
    opacity: 1,
    transition: { duration: 0.5, delay, ease: 'easeOut' },
  }),
};

const scaleUp = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: (delay = 0) => ({
    opacity: 1,
    scale: 1,
    transition: { ...springSnappy, delay },
  }),
};

const slideFromLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: (delay = 0) => ({
    opacity: 1,
    x: 0,
    transition: { ...spring, delay },
  }),
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.15,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { ...spring },
  },
};

/* ==========================================
   REUSABLE ANIMATED SECTION HEADER
   ========================================== */
function SectionHeader({ tag, title, subtitle }) {
  return (
    <>
      <motion.div
        className="landing-section__tag"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-50px' }}
        custom={0}
      >
        {tag}
      </motion.div>
      <motion.h2
        className="landing-section__title"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-50px' }}
        custom={0.1}
      >
        {title}
      </motion.h2>
      {subtitle && (
        <motion.p
          className="landing-section__subtitle"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          custom={0.2}
        >
          {subtitle}
        </motion.p>
      )}
    </>
  );
}

/* ==========================================
   MAIN COMPONENT
   ========================================== */
function LandingPage() {
  const { t, i18n } = useTranslation();
  const [navScrolled, setNavScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Parallax refs
  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  // Hero parallax transforms
  const heroContentY = useTransform(heroProgress, [0, 1], [0, 80]);
  const orb1Y = useTransform(heroProgress, [0, 1], [0, -120]);
  const orb2Y = useTransform(heroProgress, [0, 1], [0, -80]);
  const orb3Y = useTransform(heroProgress, [0, 1], [0, -200]);

  useEffect(() => {
    const htmlOriginalStyle = {
      position: document.documentElement.style.position,
      overflow: document.documentElement.style.overflow,
      height: document.documentElement.style.height,
      width: document.documentElement.style.width,
    };
    const bodyOriginalStyle = {
      position: document.body.style.position,
      overflow: document.body.style.overflow,
      height: document.body.style.height,
      width: document.body.style.width,
    };
    const rootOriginalStyle = document.getElementById('root') ? {
      position: document.getElementById('root').style.position,
      overflow: document.getElementById('root').style.overflow,
      height: document.getElementById('root').style.height,
    } : {};

    document.documentElement.style.position = 'static';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';
    document.documentElement.style.width = '100%';

    document.body.style.position = 'static';
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    document.body.style.width = '100%';
    document.body.classList.add('landing-page-active');

    const rootEl = document.getElementById('root');
    if (rootEl) {
      rootEl.style.position = 'static';
      rootEl.style.overflow = 'visible';
      rootEl.style.height = 'auto';
    }

    return () => {
      document.documentElement.style.position = htmlOriginalStyle.position;
      document.documentElement.style.overflow = htmlOriginalStyle.overflow;
      document.documentElement.style.height = htmlOriginalStyle.height;
      document.documentElement.style.width = htmlOriginalStyle.width;

      document.body.style.position = bodyOriginalStyle.position;
      document.body.style.overflow = bodyOriginalStyle.overflow;
      document.body.style.height = bodyOriginalStyle.height;
      document.body.style.width = bodyOriginalStyle.width;
      document.body.classList.remove('landing-page-active');

      if (rootEl) {
        rootEl.style.position = rootOriginalStyle.position;
        rootEl.style.overflow = rootOriginalStyle.overflow;
        rootEl.style.height = rootOriginalStyle.height;
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setNavScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'pt-BR' ? 'en-US' : 'pt-BR';
    i18n.changeLanguage(newLang);
  };

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: <HiOutlineFilm size={22} />,
      colorClass: 'landing-feature-card__icon--purple',
      titleKey: 'landing.features.trackMovies.title',
      descKey: 'landing.features.trackMovies.description',
    },
    {
      icon: <HiOutlineCollection size={22} />,
      colorClass: 'landing-feature-card__icon--blue',
      titleKey: 'landing.features.episodeTracking.title',
      descKey: 'landing.features.episodeTracking.description',
    },
    {
      icon: <HiOutlineGlobe size={22} />,
      colorClass: 'landing-feature-card__icon--green',
      titleKey: 'landing.features.tmdbIntegration.title',
      descKey: 'landing.features.tmdbIntegration.description',
    },
    {
      icon: <span style={{ fontSize: '20px' }}>🌐</span>,
      colorClass: 'landing-feature-card__icon--cyan',
      titleKey: 'landing.features.multiLanguage.title',
      descKey: 'landing.features.multiLanguage.description',
    },
    {
      icon: <HiOutlineMoon size={22} />,
      colorClass: 'landing-feature-card__icon--orange',
      titleKey: 'landing.features.darkTheme.title',
      descKey: 'landing.features.darkTheme.description',
    },
    {
      icon: <HiOutlineDeviceMobile size={22} />,
      colorClass: 'landing-feature-card__icon--pink',
      titleKey: 'landing.features.mobilePwa.title',
      descKey: 'landing.features.mobilePwa.description',
    },
    {
      icon: <HiOutlineCog size={22} />,
      colorClass: 'landing-feature-card__icon--indigo',
      titleKey: 'landing.features.adminPanel.title',
      descKey: 'landing.features.adminPanel.description',
    },
    {
      icon: <HiOutlineMail size={22} />,
      colorClass: 'landing-feature-card__icon--red',
      titleKey: 'landing.features.emailNotifications.title',
      descKey: 'landing.features.emailNotifications.description',
    },
    {
      icon: <HiOutlineSearch size={22} />,
      colorClass: 'landing-feature-card__icon--yellow',
      titleKey: 'landing.features.search.title',
      descKey: 'landing.features.search.description',
    },
  ];

  return (
    <div
      className="landing-page"
      style={{
        position: 'static',
        overflow: 'visible',
        height: 'auto',
        minHeight: '100vh',
        width: '100%',
      }}
    >
      {/* ==========================================
          NAVBAR (with entrance animation)
          ========================================== */}
      <motion.nav
        className={`landing-nav ${navScrolled ? 'scrolled' : ''}`}
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 80, damping: 18, delay: 0.2 }}
      >
        <div className="landing-nav__inner">
          <button
            className="landing-nav__mobile-toggle"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <HiOutlineMenu size={24} />
          </button>

          <div
            className="landing-nav__logo"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
          >
            <img src="/favicon.png" alt="My Series" className="landing-nav__logo-img" />
            <span className="landing-nav__logo-text">My Series</span>
          </div>

          <div className="landing-nav__links">
            <button className="landing-nav__link" onClick={() => scrollToSection('features')}>
              {t('landing.nav.features')}
            </button>
            <button className="landing-nav__link" onClick={() => scrollToSection('how-it-works')}>
              {t('landing.nav.howItWorks')}
            </button>
            <button className="landing-nav__link" onClick={() => scrollToSection('about')}>
              {t('landing.nav.about')}
            </button>
          </div>

          <div className="landing-nav__actions">
            <button className="landing-nav__lang-btn" onClick={toggleLanguage}>
              {i18n.language === 'pt-BR' ? 'EN' : 'PT'}
            </button>
            <ThemeToggle />
            <Link to="/register" className="landing-btn landing-btn--ghost">
              {t('landing.nav.createAccount')}
            </Link>
            <Link to="/login" className="landing-btn landing-btn--primary">
              {t('landing.nav.accessSystem')}
            </Link>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu with animation */}
      {mobileMenuOpen && (
        <motion.div
          className="landing-mobile-menu"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <button
            className="landing-mobile-menu__close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <HiOutlineX size={28} />
          </button>

          <div className="landing-mobile-menu__controls">
            <button className="landing-nav__lang-btn" onClick={toggleLanguage}>
              {i18n.language === 'pt-BR' ? 'EN' : 'PT'}
            </button>
            <ThemeToggle />
          </div>

          <motion.button
            className="landing-mobile-menu__link"
            onClick={() => scrollToSection('features')}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {t('landing.nav.features')}
          </motion.button>
          <motion.button
            className="landing-mobile-menu__link"
            onClick={() => scrollToSection('how-it-works')}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {t('landing.nav.howItWorks')}
          </motion.button>
          <motion.button
            className="landing-mobile-menu__link"
            onClick={() => scrollToSection('about')}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {t('landing.nav.about')}
          </motion.button>

          <motion.div
            className="landing-mobile-menu__buttons"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Link to="/register" className="landing-btn landing-btn--ghost" onClick={() => setMobileMenuOpen(false)}>
              {t('landing.mobileMenu.createAccount')}
            </Link>
            <Link to="/login" className="landing-btn landing-btn--primary" onClick={() => setMobileMenuOpen(false)}>
              {t('landing.mobileMenu.accessSystem')}
            </Link>
          </motion.div>
        </motion.div>
      )}

      {/* ==========================================
          HERO SECTION (parallax + entrance animations)
          ========================================== */}
      <section className="landing-hero" ref={heroRef}>
        <div className="landing-hero__bg">
          <motion.div
            className="landing-hero__gradient-orb landing-hero__gradient-orb--1"
            style={{ y: orb1Y, willChange: 'transform' }}
            animate={{
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          <motion.div
            className="landing-hero__gradient-orb landing-hero__gradient-orb--2"
            style={{ y: orb2Y, willChange: 'transform' }}
            animate={{
              scale: [1, 1.08, 1],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 2,
            }}
          />
          <motion.div
            className="landing-hero__gradient-orb landing-hero__gradient-orb--3"
            style={{ y: orb3Y, willChange: 'transform' }}
            animate={{
              scale: [1, 1.15, 1],
            }}
            transition={{
              duration: 14,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 4,
            }}
          />
          <div className="landing-hero__grid" />
        </div>

        <motion.div
          className="landing-hero__content"
          style={{
            y: heroContentY,
          }}
        >
          <motion.div
            className="landing-hero__badge"
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
          >
            <span className="landing-hero__badge-dot" />
            {t('landing.hero.badge')}
          </motion.div>

          <motion.h1
            className="landing-hero__title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}
          >
            {t('landing.hero.title1')}{' '}
            <span className="landing-hero__title-highlight">{t('landing.hero.titleHighlight')}</span>
            <br />
            {t('landing.hero.title2')}
          </motion.h1>

          <motion.p
            className="landing-hero__subtitle"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35, ease: 'easeOut' }}
          >
            {t('landing.hero.subtitle')}
          </motion.p>

          <motion.div
            className="landing-hero__actions"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5, ease: 'easeOut' }}
          >
            <Link to="/register" className="landing-btn landing-btn--primary landing-btn--large landing-btn--glow">
              {t('landing.hero.cta')} &rarr;
            </Link>
            <button
              className="landing-btn landing-btn--outline-large"
              onClick={() => scrollToSection('features')}
            >
              {t('landing.hero.ctaSecondary')}
            </button>
          </motion.div>
        </motion.div>
      </section>

      {/* ==========================================
          FEATURES SECTION (stagger cards)
          ========================================== */}
      <section id="features" className="landing-section landing-features">
        <div className="landing-section__inner">
          <SectionHeader
            tag={t('landing.features.sectionTag')}
            title={t('landing.features.title')}
            subtitle={t('landing.features.subtitle')}
          />

          <motion.div
            className="landing-features__grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="landing-feature-card"
                variants={staggerItem}
                whileHover={{
                  y: -8,
                  transition: { duration: 0.3 },
                }}
              >
                <div className={`landing-feature-card__icon ${feature.colorClass}`}>
                  {feature.icon}
                </div>
                <h3 className="landing-feature-card__title">{t(feature.titleKey)}</h3>
                <p className="landing-feature-card__desc">{t(feature.descKey)}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ==========================================
          HOW IT WORKS SECTION (stagger steps)
          ========================================== */}
      <section id="how-it-works" className="landing-section landing-how">
        <div className="landing-section__inner">
          <SectionHeader
            tag={t('landing.howItWorks.sectionTag')}
            title={t('landing.howItWorks.title')}
            subtitle={t('landing.howItWorks.subtitle')}
          />

          <motion.div
            className="landing-how__steps"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            {[1, 2, 3].map((step) => (
              <motion.div key={step} className="landing-step" variants={staggerItem}>
                <div className="landing-step__number landing-step__number--glow">
                  {step}
                </div>
                <h3 className="landing-step__title">{t(`landing.howItWorks.step${step}.title`)}</h3>
                <p className="landing-step__desc">{t(`landing.howItWorks.step${step}.description`)}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ==========================================
          NERDS SECTION (slide from sides)
          ========================================== */}
      <section id="about" className="landing-section landing-nerds">
        <div className="landing-section__inner landing-nerds__layout">
          <div className="landing-nerds__left">
            <motion.div
              className="landing-section__tag"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              custom={0}
            >
              {t('landing.nerds.sectionTag')}
            </motion.div>

            <div className="landing-nerds__header-group">
              <motion.h2
                className="landing-section__title"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px' }}
                custom={0.1}
              >
                {t('landing.nerds.title')}
              </motion.h2>

              <motion.div
                className="landing-nerds__emoji landing-nerds__emoji--centered landing-nerds__emoji--wiggle"
                variants={scaleUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px' }}
                custom={0.2}
              >
                🤓
              </motion.div>
            </div>

            <motion.div
              className="landing-nerds__text"
              variants={slideFromLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
              custom={0}
            >
              <p className="landing-nerds__desc">
                {t('landing.nerds.subtitle')}
              </p>
            </motion.div>
          </div>

          <motion.div
            className="landing-nerds__grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            {[
              {
                icon: (
                  <div className="landing-nerd-card__icon landing-nerd-card__icon--no-money">
                    <HiOutlineCurrencyDollar size={30} />
                  </div>
                ),
                titleKey: 'landing.nerds.item1.title',
                descKey: 'landing.nerds.item1.description',
              },
              {
                icon: <div className="landing-nerd-card__icon">🚫</div>,
                titleKey: 'landing.nerds.item2.title',
                descKey: 'landing.nerds.item2.description',
              },
              {
                icon: <div className="landing-nerd-card__icon">❤️</div>,
                titleKey: 'landing.nerds.item3.title',
                descKey: 'landing.nerds.item3.description',
              },
              {
                icon: <div className="landing-nerd-card__icon">🚀</div>,
                titleKey: 'landing.nerds.item4.title',
                descKey: 'landing.nerds.item4.description',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="landing-nerd-card"
                variants={staggerItem}
                whileHover={{
                  y: -4,
                  transition: { duration: 0.25 },
                }}
              >
                {item.icon}
                <h4 className="landing-nerd-card__title">{t(item.titleKey)}</h4>
                <p className="landing-nerd-card__desc">{t(item.descKey)}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ==========================================
          CTA SECTION (scale + fade)
          ========================================== */}
      <section className="landing-cta">
        <div className="landing-cta__bg" />
        <div className="landing-cta__inner">
          <motion.h2
            className="landing-cta__title"
            variants={scaleUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            custom={0}
          >
            {t('landing.cta.title')}
          </motion.h2>
          <motion.p
            className="landing-cta__subtitle"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            custom={0.15}
          >
            {t('landing.cta.subtitle')}
          </motion.p>
          <motion.div
            className="landing-cta__actions"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            custom={0.3}
          >
            <Link to="/register" className="landing-btn landing-btn--primary landing-btn--large landing-btn--glow">
              {t('landing.cta.button')} &rarr;
            </Link>
          </motion.div>
          <motion.div
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0.45}
            style={{ marginTop: '20px' }}
          >
            <Link to="/login" className="landing-btn landing-btn--ghost landing-btn--large">
              {t('landing.cta.login')} &rarr;
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ==========================================
          FOOTER (fade in)
          ========================================== */}
      <motion.footer
        className="landing-footer"
        variants={fadeIn}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        custom={0}
      >
        <div className="landing-footer__inner">
          <div className="landing-footer__left">
            {t('landing.footer.madeWith')}{' '}
            <span className="landing-footer__heart">&#10084;</span>{' '}
            {t('landing.footer.by')}
          </div>
          <div className="landing-footer__free">
            <HiOutlineShieldCheck size={16} />
            {t('landing.footer.freeForever')}
          </div>
          <div className="landing-footer__right">
            {t('landing.footer.copyright')}
          </div>
        </div>
      </motion.footer>
    </div>
  );
}

export default LandingPage;
